import { QuestionnaireTemplate } from './QuestionnaireContext';
import { evaluateConditionalRules } from './templateUtils';

export interface FieldDefinition {
  label: string;
  key: string;
  isArray?: boolean;
  isFile?: boolean;
  isCustom?: boolean;
  renderer?: (value: any) => React.ReactNode;
  conditional?: (formData: any) => boolean;
  options?: { value: string; label: string }[];
}

export interface SummarySection {
  title: string;
  fields: FieldDefinition[];
  conditional?: (formData: any) => boolean;
}

/**
 * Converts a QuestionnaireTemplate into sections suitable for summary display
 */
export const convertTemplateToSummary = (
  template: QuestionnaireTemplate,
  formData: any = {}
): SummarySection[] => {
  if (!template.sections || template.sections.length === 0) {
    return [];
  }

  const sections: SummarySection[] = [];
  
  // Sort sections by order
  const sortedSections = [...template.sections].sort((a, b) => a.order - b.order);

  sortedSections.forEach(section => {
    // Skip sections that don't meet conditional rules
    if (section.conditionalRules && section.conditionalRules.length > 0) {
      const shouldShow = evaluateConditionalRules(section.conditionalRules, formData);
      if (!shouldShow) return;
    }

    // Process subsections (all questions are now within subsections)
    if (section.subsections && section.subsections.length > 0) {
      // Filter subsections based on conditional rules
      const visibleSubsections = section.subsections.filter(subsection => {
        if (!subsection.conditionalRules || subsection.conditionalRules.length === 0) {
          return true;
        }
        return evaluateConditionalRules(subsection.conditionalRules, formData);
      });

      // Only process visible subsections
      if (visibleSubsections.length > 0) {
        if (visibleSubsections.length === 1) {
          // Single subsection: use section title and combine all questions
          const subsection = visibleSubsections[0];
          const fields = createFieldsFromQuestions(subsection.questions, formData);
          
          if (fields.length > 0) {
            sections.push({
              title: section.title,
              fields: fields,
              conditional: section.conditionalRules && section.conditionalRules.length > 0
                ? (formData: any) => evaluateConditionalRules(section.conditionalRules!, formData)
                : undefined
            });
          }
        } else {
          // Multiple subsections: create a section for each visible subsection
          visibleSubsections.forEach(subsection => {
            const fields = createFieldsFromQuestions(subsection.questions, formData);
            
            if (fields.length > 0) {
              sections.push({
                title: subsection.title,
                fields: fields,
                conditional: subsection.conditionalRules && subsection.conditionalRules.length > 0
                  ? (formData: any) => evaluateConditionalRules(subsection.conditionalRules!, formData)
                  : undefined
              });
            }
          });
        }
      }
    }
  });

  return sections;
};

/**
 * Creates FieldDefinition objects from template questions
 */
const createFieldsFromQuestions = (questions: any[], formData: any = {}): FieldDefinition[] => {
  if (!questions || questions.length === 0) {
    return [];
  }

  const fields: FieldDefinition[] = [];

  questions.forEach(question => {
    // Skip questions that don't meet conditional rules
    if (question.conditionalRules && question.conditionalRules.length > 0) {
      const shouldShow = evaluateConditionalRules(question.conditionalRules, formData);
      if (!shouldShow) return;
    }

    const field: FieldDefinition = {
      label: question.title,
      key: question.id
    };

    // Set field properties based on question type
    switch (question.type) {
      case 'file':
        field.isFile = true;
        break;
      case 'checkbox':
        field.isArray = true;
        if (question.options && question.options.length > 0) {
          field.options = question.options.map((option: any) => ({
            value: option.value,
            label: option.label
          }));
        }
        break;
      case 'radio':
      case 'select':
      case 'boolean':
        if (question.options && question.options.length > 0) {
          field.options = question.options.map((option: any) => ({
            value: option.value,
            label: option.label
          }));
        } else if (question.type === 'boolean') {
          // Auto-generate Yes/No options for boolean questions
          field.options = [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ];
        }
        break;
      case 'repeater':
        field.isCustom = true;
        // The renderer for repeater fields will be set in the component
        break;
    }

    // Add conditional logic if present
    if (question.conditionalRules && question.conditionalRules.length > 0) {
      field.conditional = (formData: any) => evaluateConditionalRules(question.conditionalRules!, formData);
    }

    fields.push(field);
  });

  return fields;
};

/**
 * Maps question values to display labels using field options
 */
export const getDisplayValue = (field: FieldDefinition, value: any): string => {
  if (value === null || value === undefined || value === '') {
    return 'Not provided';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (field.options && field.options.length > 0) {
    if (field.isArray && Array.isArray(value)) {
      // Handle checkbox arrays
      return value
        .map(v => {
          const option = field.options!.find(opt => opt.value === v);
          return option ? option.label : v;
        })
        .join(', ');
    } else {
      // Handle radio/select single values
      const option = field.options.find(opt => opt.value === value);
      return option ? option.label : String(value);
    }
  }

  if (field.isArray && Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'None selected';
  }

  return String(value);
};
